import React, { useMemo } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

function getMonthKey(date) {
    return `${date.getFullYear()}-${date.getMonth()}`;
}

function buildSegments(leaves) {
    const now = new Date();
    const currentKey = getMonthKey(now);
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevKey = getMonthKey(prev);

    let currentCount = 0;
    let previousCount = 0;

    (leaves || []).forEach((leave) => {
        const rawDate =
            leave.fromDate ??
            leave.from ??
            leave.created_at ??
            leave.toDate ??
            leave.to;
        if (!rawDate) return;
        const d = typeof rawDate === "number" ? new Date(rawDate) : new Date(rawDate);
        if (Number.isNaN(d.getTime())) return;
        const key = getMonthKey(d);
        if (key === currentKey) currentCount += 1;
        if (key === prevKey) previousCount += 1;
    });

    return [
        { label: "Ce mois-ci", value: currentCount, color: "#3b82f6" },
        { label: "Mois précédent", value: previousCount, color: "#f59e0b" },
    ];
}

export default function DashboardLeaveStatusDonut({ leaves }) {
    const segments = useMemo(() => buildSegments(leaves || []), [leaves]);
    const total = segments.reduce((sum, item) => sum + item.value, 0);
    const radius = 36;
    const strokeWidth = 10;
    const circumference = 2 * Math.PI * radius;

    let offset = 0;

    return (
        <Card
            sx={{
                borderRadius: 4,
                height: "100%",
                boxShadow: "0 22px 44px rgba(15, 23, 42, 0.14)",
                border: "1px solid rgba(148, 163, 184, 0.28)",
                background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 248, 255, 0.92) 50%, rgba(255, 255, 255, 0.94) 100%)",
                overflow: "hidden",
                position: "relative",
                backdropFilter: "blur(12px)",
                transition: "transform 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 30px 68px rgba(15, 23, 42, 0.18)",
                    borderColor: "rgba(148, 163, 184, 0.45)",
                },
            }}
        >
            {/* Accent top bar */}
            <Box
                sx={{
                    height: 5,
                    background: "linear-gradient(90deg, #3b82f6, rgba(245, 158, 11, 0.85), #3b82f6)",
                }}
            />
            {/* Radial glow overlay */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        radial-gradient(circle at 15% 20%, rgba(59, 130, 246, 0.06), transparent 50%),
                        radial-gradient(circle at 85% 15%, rgba(245, 158, 11, 0.05), transparent 45%),
                        radial-gradient(circle at 50% 100%, rgba(15, 23, 42, 0.03), transparent 55%)
                    `,
                    pointerEvents: "none",
                }}
            />
            <CardContent sx={{ height: "100%", p: { xs: 2, md: 3 }, position: "relative" }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 2,
                        letterSpacing: "-0.02em",
                        color: "#1e293b",
                    }}
                >
                    Absences
                </Typography>
                <Stack direction="row" spacing={3} alignItems="center">
                    <Box
                        sx={{
                            position: "relative",
                            flexShrink: 0,
                        }}
                    >
                        {/* Subtle glow behind donut */}
                        <Box
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: 100,
                                height: 100,
                                borderRadius: "50%",
                                background: "radial-gradient(circle, rgba(59, 130, 246, 0.08), transparent 70%)",
                                pointerEvents: "none",
                            }}
                        />
                        <Box
                            component="svg"
                            viewBox="0 0 100 100"
                            sx={{ width: 120, height: 120, display: "block" }}
                        >
                            <circle
                                cx="50"
                                cy="50"
                                r={radius}
                                fill="none"
                                stroke="rgba(148, 163, 184, 0.15)"
                                strokeWidth={strokeWidth}
                            />
                            {segments.map((segment) => {
                                if (!segment.value || total === 0) return null;
                                const length = (segment.value / total) * circumference;
                                const dashArray = `${length} ${circumference - length}`;
                                const dashOffset = -offset;
                                offset += length;
                                return (
                                    <circle
                                        key={segment.label}
                                        cx="50"
                                        cy="50"
                                        r={radius}
                                        fill="none"
                                        stroke={segment.color}
                                        strokeWidth={strokeWidth}
                                        strokeDasharray={dashArray}
                                        strokeDashoffset={dashOffset}
                                        strokeLinecap="round"
                                        transform="rotate(-90 50 50)"
                                        style={{ filter: `drop-shadow(0 2px 4px ${segment.color}44)` }}
                                    />
                                );
                            })}
                            <text
                                x="50"
                                y="48"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="17"
                                fontWeight="700"
                                fill="#1e293b"
                                fontFamily="'Inter', sans-serif"
                                letterSpacing="-0.02em"
                            >
                                {total || 0}
                            </text>
                            <text
                                x="50"
                                y="63"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="9"
                                fill="rgba(107, 114, 128, 0.7)"
                                fontFamily="'Inter', sans-serif"
                                fontWeight="500"
                                letterSpacing="0.05em"
                            >
                                total
                            </text>
                        </Box>
                    </Box>
                    <Stack spacing={1.5}>
                        {segments.map((segment) => (
                            <Stack
                                key={segment.label}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: 2,
                                    background: "rgba(248, 250, 252, 0.7)",
                                    border: "1px solid rgba(148, 163, 184, 0.12)",
                                    transition: "background 200ms ease, border-color 200ms ease, transform 200ms ease",
                                    "&:hover": {
                                        background: "rgba(248, 250, 252, 1)",
                                        borderColor: "rgba(148, 163, 184, 0.28)",
                                        transform: "translateX(2px)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: segment.color,
                                        boxShadow: `0 0 6px ${segment.color}55`,
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "rgba(71, 85, 105, 0.9)",
                                        fontWeight: 500,
                                        letterSpacing: "0.01em",
                                        fontSize: "0.72rem",
                                    }}
                                >
                                    {segment.label}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "#1e293b",
                                        fontWeight: 700,
                                        fontSize: "0.75rem",
                                        ml: 0.5,
                                    }}
                                >
                                    {segment.value}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
